export type IconName =
  | "arrow-right"
  | "bars"
  | "check"
  | "chevron-down"
  | "clock"
  | "lock"
  | "moon"
  | "person"
  | "pin"
  | "play"
  | "plus"
  | "sun"
  | "trash"
  | "x";

const iconPaths: Record<IconName, string[]> = {
  "arrow-right": ["M5 12h14", "m14 0-5-5", "m5 5-5 5"],
  bars: ["M5 19V10", "M12 19V5", "M19 19v-7"],
  check: ["m5 12 4 4L19 6"],
  "chevron-down": ["m7 9 5 5 5-5"],
  clock: ["M12 5a7 7 0 1 0 7 7", "M12 8v4l3 2"],
  lock: ["M9 5H5v14h4", "m14-4 4-3-4-3", "M18 12H9"],
  moon: ["M19 15.5A8 8 0 0 1 8.5 5 7 7 0 1 0 19 15.5Z"],
  person: ["M12 5a3 3 0 1 1 0 6 3 3 0 0 1 0-6Z", "M5.5 20c.8-3.3 3-5 6.5-5s5.7 1.7 6.5 5"],
  pin: ["m9 4 6 0-1 5 3 3H7l3-3-1-5Z", "M12 12v8"],
  play: ["m8 5 11 7-11 7V5Z"],
  plus: ["M12 5v14", "M5 12h14"],
  sun: ["M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z", "M12 2v2", "M12 20v2", "M4.93 4.93l1.42 1.42", "m17.66 17.66 1.41 1.41", "M2 12h2", "M20 12h2", "m4.93 19.07 1.42-1.42", "m17.66 6.34 1.41-1.41"],
  trash: ["M5 7h14", "m9 0-1-3h-4L9 7", "m-2 0 1 13h8l1-13", "M10 11v5", "M14 11v5"],
  x: ["M6 6l12 12", "M18 6 6 18"],
};

const svgNamespace = "http://www.w3.org/2000/svg";

export function icon(name: IconName): SVGSVGElement {
  const svg = document.createElementNS(svgNamespace, "svg");
  svg.classList.add("ui-icon");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  for (const data of iconPaths[name]) {
    const path = document.createElementNS(svgNamespace, "path");
    path.setAttribute("d", data);
    svg.append(path);
  }
  return svg;
}

export function setIcon(container: Element, name: IconName): void {
  container.replaceChildren(icon(name));
}

export function hydrateIcons(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>("[data-ui-icon]").forEach((container) => {
    const name = container.dataset.uiIcon as IconName;
    if (name in iconPaths) setIcon(container, name);
  });
}

export interface DataChipOptions {
  label: string;
  color: string;
  selected?: boolean;
  interactive?: boolean;
  onToggle?: () => void;
}

export interface ChipActionOptions {
  icon: IconName;
  text?: string;
  label: string;
  expanded?: boolean;
  controls?: string;
  onActivate?: () => void;
}

export function createChipAction(options: ChipActionOptions): HTMLButtonElement {
  const action = document.createElement("button");
  action.type = "button";
  action.className = "ui-chip ui-chip-action";
  action.setAttribute("aria-label", options.label);
  if (options.expanded !== undefined) action.setAttribute("aria-expanded", String(options.expanded));
  if (options.controls) action.setAttribute("aria-controls", options.controls);
  action.append(icon(options.icon));
  if (options.text) action.append(document.createTextNode(options.text));
  action.addEventListener("click", () => options.onActivate?.());
  return action;
}

export function createDataChip(options: DataChipOptions): HTMLElement {
  const chip = document.createElement(options.interactive === false ? "span" : "button");
  chip.className = "ui-chip ui-chip-data";
  chip.style.setProperty("--chip-color", options.color);
  const dot = document.createElement("span");
  dot.className = "ui-chip-dot";
  dot.setAttribute("aria-hidden", "true");
  const label = document.createElement("span");
  label.textContent = options.label;
  chip.append(dot, label);
  if (chip instanceof HTMLButtonElement) {
    chip.type = "button";
    chip.setAttribute("aria-pressed", String(Boolean(options.selected)));
    chip.addEventListener("click", () => options.onToggle?.());
  } else if (options.selected) {
    chip.dataset.selected = "true";
  }
  return chip;
}

export interface InlineCreatorOptions {
  actionText: string;
  actionLabel?: string;
  inputLabel: string;
  submitLabel: string;
  placeholder: string;
  emptyError: string;
  maxLength?: number;
  onCommit: (value: string) => void | Promise<void>;
}

export interface InlineCreator {
  element: HTMLElement;
  begin(): void;
  cancel(): void;
}

export interface InlineTextEditOptions {
  trigger: HTMLButtonElement;
  value: string;
  label: string;
  inputClassName: string;
  maxLength?: number;
  onCommit: (value: string) => void | Promise<void>;
  onError?: (error: unknown) => void;
  onAfterClose?: () => void;
}

export function beginInlineTextEdit(options: InlineTextEditOptions): HTMLInputElement {
  const input = document.createElement("input");
  input.className = options.inputClassName;
  input.value = options.value;
  input.size = Math.min(Math.max([...options.value].length, 4), options.maxLength ?? 40);
  input.autocomplete = "off";
  input.setAttribute("aria-label", options.label);
  if (options.maxLength) input.maxLength = options.maxLength;
  options.trigger.replaceWith(input);

  let settled = false;
  let committing = false;
  const close = (): void => {
    if (input.isConnected) input.replaceWith(options.trigger);
    options.onAfterClose?.();
  };
  const cancel = (): void => {
    if (settled) return;
    settled = true;
    close();
  };
  const commit = async (): Promise<void> => {
    if (settled || committing) return;
    committing = true;
    input.removeAttribute("aria-invalid");
    try {
      await options.onCommit(input.value);
      settled = true;
      close();
    } catch (error) {
      committing = false;
      input.setAttribute("aria-invalid", "true");
      options.onError?.(error);
      requestAnimationFrame(() => { input.focus(); input.select(); });
    }
  };
  input.addEventListener("keydown", (event) => {
    if (event.key === "Enter") { event.preventDefault(); void commit(); }
    if (event.key === "Escape") { event.preventDefault(); cancel(); }
  });
  input.addEventListener("blur", () => { void commit(); });
  requestAnimationFrame(() => { input.focus(); input.select(); });
  return input;
}

export function createInlineCreator(options: InlineCreatorOptions): InlineCreator {
  const root = document.createElement("div");
  root.className = "ui-inline-create";
  const action = createChipAction({
    icon: "plus",
    text: options.actionText,
    label: options.actionLabel ?? options.actionText,
  });
  const form = document.createElement("form");
  form.className = "ui-inline-create-form";
  form.hidden = true;
  const input = document.createElement("input");
  input.className = "ui-input ui-inline-create-input";
  if (options.maxLength) input.maxLength = options.maxLength;
  input.autocomplete = "off";
  input.placeholder = options.placeholder;
  input.setAttribute("aria-label", options.inputLabel);
  const submit = document.createElement("button");
  submit.type = "submit";
  submit.className = "ui-button ui-button-icon ui-button-accent";
  submit.setAttribute("aria-label", options.submitLabel);
  submit.append(icon("check"));
  const dismiss = document.createElement("button");
  dismiss.type = "button";
  dismiss.className = "ui-button ui-button-icon ui-button-quiet";
  dismiss.setAttribute("aria-label", "Cancel");
  dismiss.append(icon("x"));
  const error = document.createElement("span");
  error.className = "ui-inline-error";
  error.setAttribute("role", "status");
  error.setAttribute("aria-live", "polite");
  form.append(input, submit, dismiss, error);
  root.append(action, form);

  const cancel = (restoreFocus = true): void => {
    input.value = "";
    error.textContent = "";
    form.hidden = true;
    action.hidden = false;
    if (restoreFocus) action.focus();
  };
  dismiss.addEventListener("click", () => cancel());
  const begin = (): void => {
    action.hidden = true;
    form.hidden = false;
    requestAnimationFrame(() => input.focus());
  };
  action.addEventListener("click", begin);
  input.addEventListener("keydown", (event) => {
    if (event.key === "Escape") { event.preventDefault(); cancel(); }
  });
  input.addEventListener("blur", () => {
    queueMicrotask(() => { if (!root.contains(document.activeElement) && !input.value.trim()) cancel(false); });
  });
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const value = input.value.trim();
    if (!value) { error.textContent = options.emptyError; input.focus(); return; }
    submit.disabled = true;
    error.textContent = "";
    try {
      await options.onCommit(value);
      if (root.isConnected) cancel(false);
    } catch (caught) {
      error.textContent = caught instanceof Error ? caught.message : "Could not create this item.";
      input.select();
    } finally {
      submit.disabled = false;
    }
  });
  return { element: root, begin, cancel: () => cancel(true) };
}

export function nextOptionIndex(current: number, direction: 1 | -1, count: number): number {
  if (count <= 0) return -1;
  return (current + direction + count) % count;
}

interface SelectControl {
  select: HTMLSelectElement;
  trigger: HTMLButtonElement;
  menu: HTMLElement;
  refresh(): void;
  close(restoreFocus?: boolean): void;
}

let openSelect: SelectControl | null = null;
let generatedControlId = 0;
const selectControls = new WeakMap<HTMLSelectElement, SelectControl>();
const segmentControls = new WeakMap<HTMLSelectElement, { refresh(): void }>();

function enhanceSelect(select: HTMLSelectElement): SelectControl {
  const existing = selectControls.get(select);
  if (existing) return existing;
  if (!select.id) select.id = `ui-select-${++generatedControlId}`;
  const shell = document.createElement("div");
  shell.className = "ui-select";
  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.className = "ui-select-trigger";
  trigger.id = `${select.id}-trigger`;
  trigger.setAttribute("aria-haspopup", "listbox");
  trigger.setAttribute("aria-expanded", "false");
  const value = document.createElement("span");
  value.className = "ui-select-value";
  value.id = `${select.id}-value`;
  const affordance = document.createElement("span");
  affordance.className = "ui-select-affordance";
  affordance.append(icon("chevron-down"));
  trigger.append(value, affordance);
  const label = select.closest(".ui-field")?.querySelector<HTMLElement>(".ui-label");
  if (label) {
    label.id ||= `${select.id}-label`;
    trigger.setAttribute("aria-labelledby", `${label.id} ${value.id}`);
  }
  const menu = document.createElement("div");
  menu.className = "ui-select-menu";
  menu.id = `${select.id}-menu`;
  menu.setAttribute("role", "listbox");
  menu.setAttribute("aria-labelledby", label?.id || trigger.id);
  menu.hidden = true;
  trigger.setAttribute("aria-controls", menu.id);
  shell.append(trigger, menu);
  select.after(shell);
  select.classList.add("ui-select-source");
  select.dataset.uiEnhanced = "true";
  select.hidden = true;
  select.tabIndex = -1;
  select.setAttribute("aria-hidden", "true");

  let activeIndex = Math.max(0, select.selectedIndex);
  const control: SelectControl = {
    select,
    trigger,
    menu,
    refresh() {
      trigger.disabled = select.disabled;
      const option = select.options[select.selectedIndex] ?? select.options[0];
      value.textContent = option?.textContent ?? "Select";
      if (activeIndex < 0 || select.options[activeIndex]?.disabled) {
        activeIndex = [...select.options].findIndex((candidate) => !candidate.disabled);
      }
      [...menu.querySelectorAll<HTMLElement>("[role=option]")].forEach((item, index) => {
        const sourceOption = select.options[index];
        const selected = sourceOption?.selected === true;
        const active = index === activeIndex;
        (item as HTMLButtonElement).disabled = select.disabled || sourceOption?.disabled === true;
        item.setAttribute("aria-selected", String(selected));
        item.dataset.active = String(active);
        item.tabIndex = active ? 0 : -1;
      });
    },
    close(restoreFocus = false) {
      menu.hidden = true;
      trigger.setAttribute("aria-expanded", "false");
      shell.dataset.open = "false";
      if (openSelect === control) openSelect = null;
      if (restoreFocus) trigger.focus();
    },
  };

  const focusOption = (index: number): void => {
    if (index < 0 || select.options[index]?.disabled) return;
    activeIndex = index;
    control.refresh();
    menu.querySelector<HTMLElement>(`[data-index="${index}"]`)?.focus();
  };
  const edgeOption = (fromEnd = false): number => {
    const indexes = [...select.options].map((_, index) => index);
    if (fromEnd) indexes.reverse();
    return indexes.find((index) => !select.options[index]?.disabled) ?? -1;
  };
  const choose = (index: number): void => {
    const option = select.options[index];
    if (!option || option.disabled) return;
    select.value = option.value;
    activeIndex = index;
    control.refresh();
    control.close(true);
    select.dispatchEvent(new Event("change", { bubbles: true }));
  };
  const open = (): void => {
    openSelect?.close();
    openSelect = control;
    activeIndex = Math.max(0, select.selectedIndex);
    menu.hidden = false;
    trigger.setAttribute("aria-expanded", "true");
    shell.dataset.open = "true";
    control.refresh();
    focusOption(activeIndex);
  };
  [...select.options].forEach((option, index) => {
    const item = document.createElement("button");
    item.type = "button";
    item.className = "ui-select-option";
    item.dataset.index = String(index);
    item.setAttribute("role", "option");
    item.disabled = option.disabled;
    const label = document.createElement("span");
    label.textContent = option.textContent;
    const marker = document.createElement("span");
    marker.className = "ui-select-check";
    marker.append(icon("check"));
    item.append(label, marker);
    item.addEventListener("click", () => choose(index));
    item.addEventListener("keydown", (event) => {
      if (event.key === "Escape") { event.preventDefault(); control.close(true); return; }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const direction = event.key === "ArrowDown" ? 1 : -1;
        let next = index;
        for (let attempt = 0; attempt < select.options.length; attempt++) {
          next = nextOptionIndex(next, direction, select.options.length);
          if (!select.options[next]?.disabled) break;
        }
        focusOption(next);
      }
      if (event.key === "Home" || event.key === "End") { event.preventDefault(); focusOption(edgeOption(event.key === "End")); }
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); choose(index); }
      if (event.key === "Tab") control.close();
    });
    menu.append(item);
  });
  trigger.addEventListener("click", () => menu.hidden ? open() : control.close());
  trigger.addEventListener("keydown", (event) => {
    if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key) && menu.hidden) { event.preventDefault(); open(); }
    if (event.key === "Escape" && !menu.hidden) { event.preventDefault(); control.close(true); }
  });
  select.addEventListener("change", () => { activeIndex = Math.max(0, select.selectedIndex); control.refresh(); });
  control.refresh();
  selectControls.set(select, control);
  return control;
}

function enhanceSegment(select: HTMLSelectElement): void {
  if (segmentControls.has(select)) return;
  if (!select.id) select.id = `ui-segment-${++generatedControlId}`;
  const group = document.createElement("div");
  group.className = "ui-segment ui-segment-control";
  group.setAttribute("role", "group");
  const label = select.closest(".ui-field")?.querySelector<HTMLElement>(".ui-label");
  if (label) {
    label.id ||= `${select.id}-label`;
    group.setAttribute("aria-labelledby", label.id);
  }
  const buttons = [...select.options].map((option) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "ui-segment-item";
    button.textContent = option.textContent;
    button.addEventListener("click", () => {
      if (select.value === option.value) return;
      select.value = option.value;
      control.refresh();
      select.dispatchEvent(new Event("change", { bubbles: true }));
    });
    group.append(button);
    return button;
  });
  const control = {
    refresh(): void {
      buttons.forEach((button, index) => {
        button.disabled = select.disabled || select.options[index]?.disabled === true;
        button.setAttribute("aria-pressed", String(select.options[index]?.selected === true));
      });
    },
  };
  select.after(group);
  select.classList.add("ui-select-source");
  select.dataset.uiEnhanced = "true";
  select.hidden = true;
  select.tabIndex = -1;
  select.setAttribute("aria-hidden", "true");
  select.addEventListener("change", control.refresh);
  control.refresh();
  segmentControls.set(select, control);
}

let selectListenersInstalled = false;

export function enhanceControls(root: ParentNode = document): void {
  root.querySelectorAll<HTMLSelectElement>("select[data-ui-select]").forEach(enhanceSelect);
  root.querySelectorAll<HTMLSelectElement>("select[data-ui-segment]").forEach(enhanceSegment);
  if (selectListenersInstalled) return;
  selectListenersInstalled = true;
  document.addEventListener("pointerdown", (event) => {
    if (openSelect && !openSelect.menu.parentElement?.contains(event.target as Node)) openSelect.close();
  });
  window.addEventListener("resize", () => openSelect?.close());
}

export function syncEnhancedControls(root: ParentNode = document): void {
  root.querySelectorAll<HTMLSelectElement>("select[data-ui-select]").forEach((select) => selectControls.get(select)?.refresh());
  root.querySelectorAll<HTMLSelectElement>("select[data-ui-segment]").forEach((select) => segmentControls.get(select)?.refresh());
}
